import { useEffect, useMemo, useRef, useState } from 'react';

interface VirtualizedCodeBlockProps {
  code: string;
  className?: string;
  lineHeight?: number;
  chunkSize?: number;
  currentTheme: 'light' | 'dark';
}

const VirtualizedCodeBlock: React.FC<VirtualizedCodeBlockProps> = ({
  code,
  className,
  lineHeight = 20,
  chunkSize = 5000, // Characters per chunk
  currentTheme,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chunkRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleChunks, setVisibleChunks] = useState<Set<number>>(new Set([0]));
  const [chunkHeights, setChunkHeights] = useState<number[]>([]);

  // ... existing chunks useMemo ...
  const chunks = useMemo(() => {
    const result: string[] = [];
    for (let i = 0; i < code.length; i += chunkSize) {
      result.push(code.slice(i, i + chunkSize));
    }
    return result;
  }, [code, chunkSize]);

  // Measure actual chunk heights after render
  useEffect(() => {
    const newHeights = [...chunkHeights];
    let updated = false;
    visibleChunks.forEach((index) => {
      const ref = chunkRefs.current[index];
      if (ref) {
        const height = ref.offsetHeight;
        if (height && newHeights[index] !== height) {
          newHeights[index] = height;
          updated = true;
        }
      }
    });
    if (updated) setChunkHeights(newHeights);
    // eslint-disable-next-line
  }, [visibleChunks, chunks.length]);

  // Calculate top offsets based on measured heights
  const getTopOffset = (index: number) => {
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += chunkHeights[i] || ((chunks[i]?.length || 0) / 80) * lineHeight;
    }
    return offset;
  };

  // Calculate total height
  const estimatedHeight = useMemo(() => {
    if (chunkHeights.length === chunks.length && chunkHeights.every(Boolean)) {
      return chunkHeights.reduce((a, b) => a + b, 0);
    }
    // fallback to estimate
    const estimatedLines = code.length / 80;
    return estimatedLines * lineHeight;
  }, [chunkHeights, chunks.length, code.length, lineHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const chunkIndex = parseInt(entry.target.getAttribute('data-chunk') || '0');
            setVisibleChunks((prev) => new Set([...prev, chunkIndex]));
          }
        });
      },
      {
        root: container,
        rootMargin: '100px',
        threshold: 0,
      },
    );

    const placeholders = container.querySelectorAll('[data-chunk]');
    placeholders.forEach((placeholder) => observer.observe(placeholder));

    return () => observer.disconnect();
  }, [chunks.length]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height: '400px',
        overflowY: 'auto',
        position: 'relative',
        color: currentTheme === 'dark' ? 'var(--ui-text-color-dark)' : 'var(--ui-text-color)',
      }}
    >
      <div style={{ height: estimatedHeight, position: 'relative' }}>
        {chunks.map((chunk, index) => {
          const isVisible = visibleChunks.has(index);
          const topOffset = getTopOffset(index);

          return (
            <div
              key={index}
              data-chunk={index}
              ref={(el) => (chunkRefs.current[index] = el)}
              style={{
                position: 'absolute',
                top: topOffset,
                left: 0,
                right: 0,
                minHeight: 1, // avoid collapse
              }}
            >
              {isVisible ? (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {chunk}
                </pre>
              ) : (
                <div
                  style={{
                    height: chunkHeights[index] || (chunk.length / 80) * lineHeight,
                    backgroundColor: 'transparent',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualizedCodeBlock;
